/**
 * Reusable file-browser core (features-tier composition).
 *
 * Holds ALL browse machinery — filters, tiles (file-grid), table (data-grid over
 * a gated url-state), selection routing, the tiles⇄table view toggle + staleness
 * choreography — and is consumed by BOTH the standalone `#/file-manager` page and
 * the in-editor file-picker modal. Admin mutations (upload/delete/tag) are NOT a
 * core concern: the host injects them as `rowActions` + `onRowAction` + a
 * core-rendered action-row of `bulkActions` and drives a `refresh()` after each.
 *
 * Lives in `features/` — the tier for multi-module compositions: it orchestrates
 * sibling modules (`data-grid`, `filter-bar`) which a `modules/` file may not
 * import; `features → modules` and `pages → features` are the sanctioned edges.
 *
 * @typedef {import('../../contracts/filters.js').FilterDef} FilterDef
 * @typedef {import('../../contracts/grid.js').ColumnDef} ColumnDef
 * @typedef {import('../../contracts/grid.js').RowAction} RowAction
 * @typedef {import('../../contracts/grid.js').GridQuery} GridQuery
 * @typedef {import('../../contracts/file-selection.js').FileMeta} FileMeta
 * @typedef {import('../../contracts/file-selection.js').FileBrowseClient} FileBrowseClient
 * @typedef {import('../../core/routing/gated-url-state.js').UrlStateLike} UrlStateLike
 */

import { createGatedUrlState } from '../../core/routing/gated-url-state.js';
import { createElement } from '../../core/dom/helpers.js';
import { escapeHtml } from '../../core/dom/escape-html.js';
import { createFilterBar } from '../../modules/filter-bar/filter-bar.js';
import { createFilterRegistry } from '../../core/registry/filter-registry.js';
import { createFileGrid } from '../../components/file-grid/file-grid.js';
import { createDataGrid } from '../../modules/data-grid/data-grid.js';
import { buildAcceptMatcher, acceptToServerFilters } from '../../core/files/accept-matcher.js';
import { renderDate } from '../../core/grid/column-renderers.js';
import { safeStorageSet } from '../../core/storage/safe-storage.js';
import { isAbortError } from '../../core/http/is-abort-error.js';

/** URL keys NOT owned by the filters (grid pagination/sort + the view toggle). */
const NON_FILTER_KEYS = new Set(['page', 'pageSize', 'sortField', 'sortDir', 'view']);

/** Tile browse page size (matches the adapter default). */
const TILE_PAGE_SIZE = 500;

/**
 * Deterministic stringification of a grid-query object for change detection.
 * Array values are sorted so `?tags=3&tags=7` ≡ `?tags=7&tags=3`.
 * @param {Record<string, string | string[]>} obj
 * @returns {string}
 */
function snapshotQuery(obj) {
  const keys = Object.keys(obj).sort();
  /** @type {Record<string, string | string[]>} */
  const norm = {};
  for (const k of keys) {
    const v = obj[k];
    norm[k] = Array.isArray(v) ? [...v].map(String).sort() : v;
  }
  return JSON.stringify(norm);
}

/** Human-readable byte size. @param {unknown} bytes */
function formatSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Decorative EXT/kind chip for a non-image (or thumbnail-less) row. `aria-hidden`
 * because the adjacent name column is the row's accessible text.
 * @param {FileMeta} file
 * @returns {string} safe HTML
 */
function renderPreviewIcon(file) {
  const label = String(file.extension || file.kind || 'file').toUpperCase();
  return `<span class="file-manager__preview-icon" aria-hidden="true">${escapeHtml(label)}</span>`;
}

/**
 * Leading table-cell preview: image thumbnail for image rows, EXT/kind icon
 * otherwise. Returns safe HTML (renderHtml column) — every dynamic value escaped.
 * @param {unknown} _value
 * @param {unknown} row
 * @returns {string} safe HTML
 */
function renderPreviewCell(_value, row) {
  const file = /** @type {FileMeta} */ (row);
  const hasThumb = typeof file.thumbnail === 'string' && file.thumbnail !== '';
  const isImage = hasThumb || file.kind === 'image';
  if (!isImage) return renderPreviewIcon(file);
  const src = hasThumb ? file.thumbnail : file.url;
  if (typeof src !== 'string' || src === '') return renderPreviewIcon(file);
  return `<img class="file-manager__preview-thumb" src="${escapeHtml(src)}" alt="">`;
}

/**
 * @typedef {Object} FileBrowserBulkAction
 * @property {string} label
 * @property {'primary'|'danger'} [variant]
 * @property {boolean} [singleOnly] - Only enabled/visible when exactly one row is selected.
 * @property {(rows: FileMeta[]) => void} onClick - Receives the selected FileMeta[] (NOT ids); no auto-refresh.
 */

/**
 * @typedef {Object} FileBrowserOptions
 * @property {FileBrowseClient} browseClient - Browse client (`list`). The only browse client the core calls.
 * @property {FilterDef[]} filters - Filter definitions to render.
 * @property {import('../../contracts/filters.js').FilterDriver[]} filterDrivers
 *   The filter drivers to register — REQUIRED, no default. Each host imports + passes exactly the drivers
 *   its `filters` reference (page: all 4; modal: text+select only) so the core imports no driver module.
 * @property {UrlStateLike} urlState - URL state (real hash for the page; in-memory for the modal).
 * @property {{ debug: (msg: string, ctx?: object) => void, info: (msg: string, ctx?: object) => void, warn: (msg: string, ctx?: object) => void, error: (msg: string, ctx?: object) => void }} logger
 * @property {{ emit: (name: string, data?: unknown) => void }} eventBus
 * @property {AbortSignal} signal
 * @property {import('../../contracts/locale.js').LocaleConfig} [localeConfig]
 * @property {boolean} [selectable] - Enable selection. Default false.
 * @property {boolean} [multiple] - Allow multi-select. Default true when selectable.
 * @property {string} [accept] - Pick-mode accept gating (tiles isSelectable + table prefilter/refine).
 * @property {RowAction[]} [rowActions] - Injected admin row actions (page: edit-tags/delete; modal: []).
 * @property {(action: string, row: FileMeta) => void} [onRowAction] - Host handles the row action.
 * @property {FileBrowserBulkAction[]} [bulkActions] - Core-rendered action-row buttons.
 * @property {(rows: FileMeta[]) => void} [onSelectionChange] - Optional host hook fired (with the union) on every affordance sync.
 * @property {boolean} [persistSelectionAcrossQueries] - When true (the modal picker), the core owns the
 *   master selection and it survives filter/page/sort/view changes; the grids are visible projections.
 *   Default false (standalone page): the active grid is the selection, only uploaded rows persist.
 * @property {'tiles'|'table'} [initialView] - Default 'table'.
 * @property {boolean} [persistView] - Persist the view choice to safe-storage. Default false.
 * @property {string} [viewStorageKey] - safe-storage key when `persistView`.
 * @property {boolean} [showViewToggle] - Show the tiles/table toggle. Default true.
 * @property {HTMLElement[]} [headerExtras] - Extra header controls (e.g. the page's upload button/input).
 * @property {boolean} [chromeless] - Omit the `page-container` card chrome (padding/radius/shadow) on the
 *   root. Default false (standalone page = a page card). The modal sets true: its panel already provides
 *   the card, so `page-container` inside it would double the padding + round the inner ends.
 */

/**
 * @typedef {Object} FileBrowserHandle
 * @property {() => void} refresh - Re-fetch the active view; mark the inactive one stale.
 * @property {() => FileMeta[]} getSelectedRows - Active grid selection ∪ pendingSelected (deduped by String(id)).
 * @property {() => void} clearSelection - Clear BOTH grid selection and pendingSelected.
 * @property {(file: FileMeta) => void} addSelectedRow - Add a row to pendingSelected (idempotent by String(id)).
 * @property {() => 'tiles'|'table'} getActiveView
 * @property {() => void} destroy
 */

/**
 * Owns the selection master map + the render-reprojection guard — the subtle
 * cross-page selection machinery, behind one named interface instead of scattered
 * across the core. The grids stay the core's concern (it knows the active view); this
 * owns WHICH rows are selected and WHEN a grid emit is a genuine user change.
 *
 * Two modes, gated by `persist`:
 * - DEFAULT (standalone page): the active grid IS the selection; the map holds only
 *   uploaded rows (so they count even when filtered out). `applyUserChange` is a no-op
 *   here — the core's `getSelectedRows` unions grid ∪ map.
 * - PERSIST (modal picker): the map is the SOURCE OF TRUTH; grids are projections.
 *   `applyUserChange` diffs the rendered page into/out of the map (single=replace,
 *   multi=add/remove-rendered), but ONLY for genuine user emits — render-time prune/
 *   clear emits arrive while `reprojecting` is set and are ignored (else returning to an
 *   earlier page would delete its off-page picks).
 *
 * `add`/`clear` are the public pending/master paths used in BOTH modes.
 *
 * @param {{ multiple: boolean, persist: boolean }} opts
 */
function createSelectionProjection({ multiple, persist }) {
  /**
   * DEFAULT: uploaded rows that must count as selected even when hidden.
   * PERSIST: the master selection (all picked rows). Keyed by String(id).
   * @type {Map<string, FileMeta>}
   */
  const map = new Map();

  // True while a render-driven re-projection is in flight, so `applyUserChange` can tell
  // a prune/clear emit from a genuine user click. Save/restore (not bare false) so a tiles
  // render and a table render — or nested hooks — can't clobber each other.
  let reprojecting = false;

  return {
    /** @returns {FileMeta[]} */
    values() { return [...map.values()]; },
    keys() { return [...map.keys()]; },
    /** @param {string} key */
    has(key) { return map.has(key); },
    size() { return map.size; },
    /** @param {FileMeta} file — pending/master add (idempotent; single-mode replaces). */
    add(file) {
      if (file == null || file.id == null) return;
      if (!multiple) map.clear(); // single-select holds at most one
      map.set(String(file.id), file);
    },
    clear() { map.clear(); },

    /**
     * A genuine grid selection change → mutate the master (PERSIST only). No-op in
     * DEFAULT mode (grid is the selection) and while reprojecting (prune/clear emit).
     * @param {(string|number)[]} visibleSelectedIds
     * @param {FileMeta[]} currentRows
     */
    applyUserChange(visibleSelectedIds, currentRows) {
      if (!persist || reprojecting) return;
      const sel = new Set(visibleSelectedIds.map(String));
      if (!multiple) {
        // Single-select: a user pick REPLACES the master (picking on a new page evicts
        // the prior pick — a single field can never accumulate two rows).
        map.clear();
        for (const row of currentRows) {
          if (sel.has(String(row.id))) map.set(String(row.id), row);
        }
      } else {
        for (const row of currentRows) {
          const key = String(row.id);
          if (sel.has(key)) map.set(key, row);
          else map.delete(key); // rendered-but-unselected → removed
        }
      }
    },

    isReprojecting() { return reprojecting; },

    /**
     * Run `fn` with the reprojection guard set (synchronous renders — tiles). Save/restore.
     * @param {() => void} fn
     */
    duringRender(fn) {
      const prev = reprojecting;
      reprojecting = true;
      try { fn(); } finally { reprojecting = prev; }
    },

    /**
     * Begin an async render (table): set the guard, return a cleanup that restores it.
     * Run the cleanup in a `finally` so the guard releases even if the render throws.
     * @returns {() => void}
     */
    beginRender() {
      const prev = reprojecting;
      reprojecting = true;
      return () => { reprojecting = prev; };
    },
  };
}

/**
 * Mount the file-browser core into `container`.
 *
 * @param {HTMLElement} container
 * @param {FileBrowserOptions} options
 * @returns {FileBrowserHandle}
 */
export function createFileBrowser(container, options) {
  const {
    browseClient,
    filters,
    filterDrivers,
    urlState,
    logger,
    eventBus,
    signal,
    localeConfig,
    selectable = false,
    multiple = true,
    accept = '',
    rowActions = [],
    onRowAction,
    bulkActions = [],
    onSelectionChange: hostOnSelectionChange,
    initialView = 'table',
    persistView = false,
    viewStorageKey,
    showViewToggle = true,
    headerExtras = [],
    chromeless = false,
  } = options;

  const isSelectable = buildAcceptMatcher(accept);

  let destroyed = false;
  /** @type {Array<() => void>} */
  const cleanups = [];

  // Host-supplied filter drivers only (the core imports no driver module).
  const filterReg = createFilterRegistry();
  for (const driver of filterDrivers) filterReg.register(driver);

  /** @type {'tiles'|'table'} */
  let activeView = initialView === 'tiles' ? 'tiles' : 'table';

  // Only clear selection on a user filter change once a view has rendered.
  let hasRenderedOnce = false;

  // --- DOM scaffold ---
  // `page-container` gives the standalone page its card chrome; the modal passes chromeless:true
  // because its own panel already provides the card (else the inner ends round + padding doubles).
  const page = createElement('div', { class: chromeless ? 'file-manager' : 'page-container file-manager' });
  const headerRow = createElement('div', { class: 'page-header' });

  // View toggle (2-button group; aria-pressed per policies.js).
  const viewToggle = createElement('div', { class: 'file-manager__view-toggle', role: 'group', 'aria-label': 'View' });
  const tilesToggleBtn = createElement('button', { class: 'btn file-manager__view-btn', type: 'button', 'data-view': 'tiles' }, ['Tiles']);
  const tableToggleBtn = createElement('button', { class: 'btn file-manager__view-btn', type: 'button', 'data-view': 'table' }, ['Table']);
  viewToggle.appendChild(tilesToggleBtn);
  viewToggle.appendChild(tableToggleBtn);
  if (showViewToggle) headerRow.appendChild(viewToggle);
  for (const el of headerExtras) headerRow.appendChild(el);

  const filterContainer = createElement('div', { class: 'filter-bar-container' });
  const actionsRow = createElement('div', { class: 'file-manager__actions' });
  const selectionInfo = createElement('span', { class: 'file-manager__selection-info' });
  actionsRow.appendChild(selectionInfo);
  /** @type {{ def: FileBrowserBulkAction, btn: HTMLButtonElement }[]} */
  const bulkButtons = [];
  for (const def of bulkActions) {
    const btn = /** @type {HTMLButtonElement} */ (createElement('button', {
      class: `file-manager__primary-action btn ${def.variant === 'danger' ? 'btn-danger' : def.variant === 'primary' ? 'btn-primary' : ''}`.trim(),
      type: 'button',
    }, [def.label]));
    btn.addEventListener('click', () => {
      const rows = getSelectedRows();
      if (rows.length === 0) return;
      if (def.singleOnly && rows.length !== 1) return;
      def.onClick(rows);
    });
    actionsRow.appendChild(btn);
    bulkButtons.push({ def, btn });
  }
  actionsRow.style.display = 'none';

  const tilesContainer = createElement('div', { class: 'file-manager__grid-container' });
  const tableContainer = createElement('div', { class: 'file-manager__table-container' });
  const statusEl = createElement('div', { class: 'file-manager__status' });

  page.appendChild(headerRow);
  page.appendChild(filterContainer);
  page.appendChild(actionsRow);
  page.appendChild(statusEl);
  page.appendChild(tilesContainer);
  page.appendChild(tableContainer);
  container.appendChild(page);

  // --- Filters ---
  const filterBar = createFilterBar(filterContainer, {
    filters,
    urlState,
    logger,
    filterRegistry: filterReg,
    localeConfig,
  });

  // ---------------------------------------------------------------------------
  // Selection. Two modes (see `persistSelectionAcrossQueries`):
  //
  //  • DEFAULT (standalone page): the active grid IS the selection; the only
  //    cross-query survivor is `pendingSelected` (uploaded rows). getSelectedRows
  //    = grid selection ∪ pendingSelected. Filter/view changes clear the grid
  //    selection (a fresh result set), as the file-manager always did.
  //
  //  • PERSIST (in-editor modal): the core owns `selectedRows` (Map<id,FileMeta>)
  //    as the source of truth; the grids are visible-page PROJECTIONS. A grid
  //    selection change diffs the rendered page into/out of the master map;
  //    uploaded rows are added directly. getSelectedRows = the whole master map.
  //    After every render the master is re-projected onto the just-rendered grid
  //    so a returning page shows its checkmarks. Picks survive filter/page/sort/
  //    view switch; cleared only by clearSelection().
  // ---------------------------------------------------------------------------
  const persist = Boolean(options.persistSelectionAcrossQueries);

  /** @type {FileMeta[]} */
  let currentRows = [];
  // Selection master + render-reprojection guard live behind one named interface
  // (see createSelectionProjection). The core keeps grid-facing concerns (it knows
  // the active view); the projection owns WHICH rows are selected and WHEN a grid emit
  // counts as a genuine user change vs. a render-time prune.
  const selection = createSelectionProjection({ multiple, persist });

  function activeSelectionIds() {
    return activeView === 'table'
      ? (tableGrid ? tableGrid.getSelectedRows().map((/** @type {any} */ r) => r.id) : [])
      : fileGrid.getSelectedIds();
  }

  function clearActiveSelection() {
    if (activeView === 'table') tableGrid?.clearSelection();
    else fileGrid.clearSelection();
  }

  function getSelectedRows() {
    if (persist) return selection.values();
    // Default: grid-selected rows (active view) UNION the projection map (uploads).
    const ids = new Set(activeSelectionIds().map(String));
    /** @type {FileMeta[]} */
    const out = currentRows.filter(r => ids.has(String(r.id)));
    const seen = new Set(out.map(r => String(r.id)));
    for (const file of selection.values()) {
      const key = String(file.id);
      if (!seen.has(key)) { out.push(file); seen.add(key); }
    }
    return out;
  }

  /** Re-project the master selection onto the just-rendered grid (PERSIST only). */
  function reapplySelectionToGrid() {
    if (!persist) return;
    const keys = selection.keys();
    if (activeView === 'table') tableGrid?.setSelectedKeys(keys);
    else fileGrid.setSelectedIds(keys);
  }

  /**
   * A grid selection change. PERSIST: the projection diffs the rendered page into/out
   * of the master (off-page rows preserved; render-prune emits ignored). DEFAULT: the
   * projection no-ops and we just refresh affordances (grid ∪ uploads union).
   * @param {(string|number)[]} visibleSelectedIds
   */
  function onGridSelectionChange(visibleSelectedIds) {
    selection.applyUserChange(visibleSelectedIds, currentRows);
    syncSelectionAffordances();
  }

  /** THE single action-row update path — always reads the effective selection. */
  function syncSelectionAffordances() {
    const rows = getSelectedRows();
    const n = rows.length;
    if (n > 0) {
      selectionInfo.textContent = `${n} selected`;
      actionsRow.style.display = '';
      for (const { def, btn } of bulkButtons) {
        btn.style.display = (def.singleOnly && n !== 1) ? 'none' : '';
      }
    } else {
      actionsRow.style.display = 'none';
    }
    hostOnSelectionChange?.(rows);
  }

  /** @param {FileMeta} file */
  function addSelectedRow(file) {
    // `selection.add` is the shared pending/master path (idempotent; single-mode
    // replaces — the invariant is structural in the projection, not at this call site).
    selection.add(file);
    syncSelectionAffordances();
  }

  function clearSelection() {
    selection.clear();
    clearActiveSelection();
    syncSelectionAffordances();
  }

  // ---------------------------------------------------------------------------
  // Tiles view
  // ---------------------------------------------------------------------------
  const fileGrid = createFileGrid(tilesContainer, {
    selectable,
    multiple,
    isSelectable,
    onSelectionChange: (ids) => { if (activeView === 'tiles') onGridSelectionChange(ids); },
    onActivate: undefined,
  });

  /** @param {Record<string, string | string[]>} params */
  function readFilters(params) {
    /** @type {Record<string, unknown>} */
    const result = {};
    for (const [k, v] of Object.entries(params)) {
      if (NON_FILTER_KEYS.has(k)) continue;
      result[k] = v;
    }
    return result;
  }

  let tilesVersion = 0;
  function refreshTiles() {
    const thisVersion = ++tilesVersion;
    fileGrid.setBusy(true);
    const fltrs = readFilters(urlState.toQueryObject());
    browseClient.list({ filters: fltrs, page: 1, pageSize: TILE_PAGE_SIZE }, { signal })
      .then((result) => {
        if (destroyed || thisVersion !== tilesVersion) return;
        currentRows = result.rows;
        // tiles render is synchronous — its drop-emit fires inside render(); guard it so the
        // prune isn't read as a user deselection, then re-project (silent) this page's checkmarks.
        selection.duringRender(() => {
          fileGrid.render(result.rows);
          reapplySelectionToGrid(); // PERSIST: restore this page's checkmarks from the master
        });
        fileGrid.setBusy(false);
        hasRenderedOnce = true;
        if (activeView === 'tiles') {
          statusEl.textContent = result.rows.length === 0 ? 'No files found.' : '';
          syncSelectionAffordances(); // keep count correct if the grid pruned/emitted
        }
      })
      .catch((/** @type {unknown} */ err) => {
        if (destroyed || thisVersion !== tilesVersion) return;
        fileGrid.setBusy(false);
        if (isAbortError(err)) return;
        logger.error('File list failed', { data: err });
        eventBus.emit('toast.message.requested', { message: 'Failed to load files', type: 'error' });
      });
  }

  // ---------------------------------------------------------------------------
  // Table view (lazy-mounted over a gated urlState)
  // ---------------------------------------------------------------------------
  const tableGate = createGatedUrlState(urlState, { active: false });
  /** @type {(import('../../contracts/module.js').MountedModule & { refresh: () => void, getSelectedRows: () => unknown[], clearSelection: () => void, setSelectedKeys: (keys: string[]) => void }) | null} */
  let tableGrid = null;
  /** @type {string} */
  let lastTableQuerySnapshot = '';
  let tableQueryStale = false;
  let tableDataStale = false;
  let tableFetchVersion = 0;

  /** @type {ColumnDef[]} */
  const TABLE_COLUMNS = [
    { key: 'preview', label: '', sortable: false, renderHtml: true, render: renderPreviewCell },
    { key: 'name', label: 'File name', sortable: true },
    { key: 'kind', label: 'Type', sortable: true, render: (v) => (v ? String(v).charAt(0).toUpperCase() + String(v).slice(1) : '') },
    { key: 'size', label: 'Size', sortable: true, render: (v) => formatSize(v) },
    { key: 'created', label: 'Created', sortable: true, renderHtml: true, render: (v) => renderDate(v, localeConfig) },
    {
      key: 'tags', label: 'Tags', sortable: false, renderHtml: true,
      render: (v) => Array.isArray(v)
        ? v.map((/** @type {any} */ t) => `<span class="file-manager__tag-chip">${escapeHtml(String(t?.name ?? ''))}</span>`).join(' ')
        : '',
    },
  ];

  const pickMode = bulkActions.some(b => b.variant === 'primary');
  /** Server prefilter derived from accept (or null). */
  const acceptServerFilter = accept ? acceptToServerFilters(accept) : null;

  /**
   * data-grid fetchPage adapter → browseClient.list. Applies accept prefilter
   * (server) + matcher refine (client). Keeps snapshot + currentRows in sync.
   * @param {GridQuery} query
   * @param {AbortSignal} [sig]
   */
  function tableFetchPage(query, sig) {
    const thisFetch = ++tableFetchVersion;
    /** @type {Record<string, unknown>} */
    const fltrs = { ...(query.filters ?? {}) };

    const isConflict = acceptServerFilter != null
      && fltrs.kind != null
      && String(fltrs.kind) !== acceptServerFilter.kind;

    if (acceptServerFilter && fltrs.kind == null) fltrs.kind = acceptServerFilter.kind;

    /** @type {Promise<import('../../contracts/file-selection.js').FileGridPage>} */
    const source = isConflict
      ? Promise.resolve({ rows: [], total: 0, page: query.page, pageSize: query.pageSize })
      : browseClient.list({
        filters: fltrs,
        page: query.page,
        pageSize: query.pageSize,
        sortField: query.sortField,
        sortDir: query.sortDir,
      }, { signal: sig });

    return source.then((result) => {
      const matcher = accept ? buildAcceptMatcher(accept) : null;
      const rows = matcher ? result.rows.filter(matcher) : result.rows;
      if (!destroyed && thisFetch === tableFetchVersion) {
        currentRows = rows;
        lastTableQuerySnapshot = snapshotQuery(urlState.toQueryObject());
        if (activeView === 'table') statusEl.textContent = rows.length === 0 ? 'No files found.' : '';
      }
      return { rows, total: result.total, page: result.page, pageSize: result.pageSize };
    });
  }

  /** @param {string} action @param {unknown} row */
  function handleRowAction(action, row) {
    onRowAction?.(action, /** @type {FileMeta} */ (row));
  }

  function mountTable() {
    tableGate.setActive(true);
    tableGrid = createDataGrid(tableContainer, {
      columns: TABLE_COLUMNS,
      fetchPage: tableFetchPage,
      urlState: tableGate,
      logger,
      signal,
      rowKey: 'id',
      rowActions,
      onRowAction: handleRowAction,
      selectable,
      multiple,
      onSelectionChange: (rows) => {
        if (activeView === 'table') onGridSelectionChange(rows.map((/** @type {any} */ r) => r.id));
      },
      onBeforeRender: () => {
        // PERSIST: the table's prune-on-render emit fires during table.update — set the guard
        // here and release it in the returned cleanup (run in data-grid's finally, even on throw),
        // so the prune isn't read as a user deselection.
        if (!persist || activeView !== 'table') return undefined;
        return selection.beginRender();
      },
      onRendered: () => {
        // PERSIST: re-project the master onto the new page; keep the count right. Runs inside the
        // onBeforeRender guard window (released by its cleanup), so reapply's silent setter is safe.
        if (activeView === 'table') { reapplySelectionToGrid(); syncSelectionAffordances(); }
      },
      pageSizeOptions: [25, 50, 100],
      stickyHeader: true,
      // Stack rows into labeled cards on mobile (<768px) so the narrow modal panel
      // doesn't overflow horizontally and the preview thumbnail stays visible. Also
      // improves the standalone #/file-manager table on mobile.
      mobileMode: 'responsive',
      onError: (/** @type {Error} */ err) => { if (!isAbortError(err)) logger.error('Table load failed', { data: err }); },
    });
  }

  // ---------------------------------------------------------------------------
  // View toggle + switch
  // ---------------------------------------------------------------------------
  function syncToggleButtons() {
    tilesToggleBtn.setAttribute('aria-pressed', String(activeView === 'tiles'));
    tableToggleBtn.setAttribute('aria-pressed', String(activeView === 'table'));
    tilesContainer.style.display = activeView === 'tiles' ? '' : 'none';
    tableContainer.style.display = activeView === 'table' ? '' : 'none';
  }

  /** @param {'tiles'|'table'} next */
  function switchView(next) {
    if (next === activeView) return;
    // PERSIST mode keeps the master selection across the view switch (the new
    // view re-projects it on render); DEFAULT mode clears the leaving grid's
    // selection. Skipping clearActiveSelection in persist also avoids the
    // grid's clear-emit diff-deleting the current page's picks.
    if (next === 'table') {
      if (!persist) clearActiveSelection(); // clears tiles
      activeView = 'table';
      syncToggleButtons();
      if (!tableGrid) {
        mountTable();
        lastTableQuerySnapshot = snapshotQuery(urlState.toQueryObject());
      } else {
        tableGate.setActive(true);
        if (tableQueryStale) { tableGate.flush(); tableQueryStale = false; tableDataStale = false; }
        else if (tableDataStale) { tableGrid.refresh(); tableDataStale = false; }
        // PERSIST: if we return to an already-current table (no re-fetch above, hence no onRendered
        // re-projection), the visible checkmarks may lag the master (e.g. picks made in tiles). Sync
        // them now — silent setter, no emit. (Tiles always re-fetch on switch, so they self-project.)
        else reapplySelectionToGrid();
      }
    } else {
      tableGate.setActive(false);
      if (!persist) clearActiveSelection(); // clears table
      activeView = 'tiles';
      syncToggleButtons();
      refreshTiles();
    }
    // Affordance recompute (union/master) — replaces the old raw onSelectionChange([]).
    syncSelectionAffordances();
    if (persistView && viewStorageKey) safeStorageSet(viewStorageKey, activeView);
  }

  const onTilesToggle = () => switchView('tiles');
  const onTableToggle = () => switchView('table');
  tilesToggleBtn.addEventListener('click', onTilesToggle);
  tableToggleBtn.addEventListener('click', onTableToggle);
  cleanups.push(() => tilesToggleBtn.removeEventListener('click', onTilesToggle));
  cleanups.push(() => tableToggleBtn.removeEventListener('click', onTableToggle));

  // ---------------------------------------------------------------------------
  // urlState subscription — tiles + selection + table staleness
  // ---------------------------------------------------------------------------
  let lastFilterSnapshot = snapshotQuery(/** @type {any} */ (readFilters(urlState.toQueryObject())));

  const unsubscribe = urlState.subscribe((params) => {
    const filterSnap = snapshotQuery(/** @type {any} */ (readFilters(params)));
    const filterChanged = filterSnap !== lastFilterSnapshot;
    lastFilterSnapshot = filterSnap;

    if (activeView !== 'table') {
      const fullSnap = snapshotQuery(params);
      if (tableGrid && fullSnap !== lastTableQuerySnapshot) tableQueryStale = true;
    }

    if (!filterChanged) return;

    // DEFAULT mode: a filter change clears the grid selection (fresh result set),
    // as the file-manager always did. PERSIST mode: the master is the source of
    // truth and survives filter changes — do NOT clear (clearActiveSelection emits
    // a grid selection-change that would diff-delete the current page's picks);
    // the grid re-projects the master on the next render.
    if (hasRenderedOnce && !persist) clearActiveSelection();
    // Single affordance path (union/master) — never a raw onSelectionChange([]).
    syncSelectionAffordances();
    if (activeView === 'tiles') refreshTiles();
  });
  cleanups.push(unsubscribe);

  // ---------------------------------------------------------------------------
  // Public: refresh (host calls after upload/delete/tag mutations)
  // ---------------------------------------------------------------------------
  function refresh() {
    if (activeView === 'table') tableGrid?.refresh();
    else { tableDataStale = true; refreshTiles(); }
  }

  // --- Initial render ---
  syncToggleButtons();
  if (activeView === 'table') {
    mountTable();
    lastTableQuerySnapshot = snapshotQuery(urlState.toQueryObject());
    hasRenderedOnce = true;
  } else {
    refreshTiles();
  }

  // Silence the unused-in-some-builds warning while documenting intent: pickMode
  // is derived for parity with the page's accept handling; the matcher already
  // gates regardless. Referenced here to keep the binding meaningful.
  void pickMode;

  return {
    refresh,
    getSelectedRows,
    clearSelection,
    addSelectedRow,
    getActiveView: () => activeView,
    destroy() {
      destroyed = true;
      tilesVersion++;
      for (const fn of cleanups) fn();
      cleanups.length = 0;
      fileGrid.destroy();
      if (tableGrid) tableGrid.destroy();
      tableGate.destroy();
      filterBar.destroy();
      page.remove();
    },
  };
}

export default createFileBrowser;
